import Media, { MediaFolder } from "@/types/Media.interface"
import Pagination from "@/types/Pagination.interface"
import { MediaParams } from "@/types/PaginationParams.interface"

/**
 * TODO: We should refactor how we handle our media folders so that they are stored in separate lists
 * this way we don't just filter based on the current media and we can get a cleaner experience overall
 * 
 * As of 1/30/23 I'm holding off on implementing this change because I want to focus on getting AppSyncPt2
 * finished, but if I find that I have extra time I might come back and refactor it.
 */

interface State {
    mediaViewMedia: Media[]
    mediaViewMediaCurrentPage: number
    mediaViewMediaLastPage: number
    mediaViewMediaNextPage: number
    mediaViewMediaPaginating: boolean
    mediaViewFolders: MediaFolder[]
    mediaViewMediaParams: MediaParams
    mediaViewMediaDefaultParams: MediaParams
    mediaViewMediaAttachmentParams: MediaParams
    mediaViewMediaAttachmentDefaultParams: MediaParams
    mediaViewMediaUploadedParams: MediaParams
    mediaViewMediaUploadedDefaultParams: MediaParams
}

const defaults = () => {
    return {
        mediaViewMedia: [],
        mediaViewMediaCurrentPage: null,
        mediaViewMediaLastPage: null,
        mediaViewMediaNextPage: null,
        mediaViewMediaPaginating: false,
        mediaViewFolders: ['uploaded', 'attachment'],
        mediaViewMediaParams: {
            page: 1,
            sort: 'desc'
        },
        mediaViewMediaDefaultParams: {
            page: 1,
            sort: 'desc'
        },
        mediaViewMediaAttachmentParams: {
            page: 1,
            sort: 'desc'
        },
        mediaViewMediaAttachmentDefaultParams: {
            page: 1,
            sort: 'desc'
        },
        mediaViewMediaUploadedParams: {
            page: 1,
            sort: 'desc'
        },
        mediaViewMediaUploadedDefaultParams: {
            page: 1,
            sort: 'desc'
        }
    }
}
export default {
    state: defaults(),
    mutations: {
        //  Media View Media
        mediaViewMediaParamsSet (state: State, params: MediaParams): void {
            state.mediaViewMediaParams = params
        },
        mediaViewMediaAttachmentParamsSet (state: State, params: MediaParams): void {
            state.mediaViewMediaAttachmentParams = params
        },
        mediaViewMediaUploadedParamsSet (state: State, params: MediaParams): void {
            state.mediaViewMediaUploadedParams = params
        },
        mediaViewMediaPaginationPush (state: State, page: Pagination): void {
            //  If this is page one, reset our media list
            if (page.current_page === 1) state.mediaViewMedia = []

            //  set our pages
            state.mediaViewMediaCurrentPage = page.current_page
            state.mediaViewMediaLastPage = page.last_page
            state.mediaViewMediaNextPage = page.current_page + 1
        },
        mediaViewMediaPaginatingSet (state: State, bool: boolean): void {
            state.mediaViewMediaPaginating = bool
        },
        mediaViewMediaPush (state: State, medias: Media[]): void {
            //  Update our list
            state.mediaViewMedia = window.removeDuplicatesById(state.mediaViewMedia, medias)
        },
        mediaViewMediaRemove(state: State, ids: number[]): void {
            //  Get our current lists of medias
            const medias = state.mediaViewMedia

            //  If no medias, do nothing
            if (!medias) return

            //  Else, filter out our removed ids
            const filtered = medias.filter((x: Media) => !ids.includes(x.id))

            //  Set our medias to the filtered list
            state.mediaViewMedia = filtered
        },
        mediaViewMediaSet (state: State, medias: Media[]): void {
            state.mediaViewMedia = medias
        },
        mediaViewMediaSync (state: State, updates: Media[]): void {
            //  Get our media params
            const params = state.mediaViewMediaParams

            //  Check if we are sorting in descending order or not
            const desc = params.sort || 'desc'

            //  Apply our filtering
            const filtered = updates.appSyncFilter(params, 'mediaView')

            //  Filtered contains our list of items to update/push so we need a difference of items to remove
            const remove = window._.differenceBy(updates, filtered, 'id')

            //  Sync the state
            state.mediaViewMedia = window.removeDuplicatesById(state.mediaViewMedia, filtered, remove)

            //  Sort the state
            state.mediaViewMedia.sort((a: Media, b: Media) => {
                return desc ? +window.getDate(b.created_at) - +window.getDate(a.created_at) : +window.getDate(a.created_at) - +window.getDate(b.created_at)
            })
        },
        mediaViewReset (state: State): void {
            Object.assign(state, defaults())
        }
    },
    actions: {
        mediaViewMediaPaginate ({ commit, getters }): Promise<Pagination> {
            //  Grab our pagination params
            const p = getters.mediaViewMediaParams

            //  Start our pagination tracker
            commit('mediaViewMediaPaginatingSet', true)
            
            //  Assemble the API endpoint
            const url = `${getters.api}/media?${window.toUrlParams(p)}`

            //  Send the request
            return window.axios.get(url).then(response => {
                //  Set our pagination
                commit('mediaViewMediaPaginationPush', response.data)

                //  Set our media
                commit('mediaViewMediaPush', response.data.data)

                //  Stop our pagination tracker
                commit('mediaViewMediaPaginatingSet', false)

                //  Return our pagination result
                return response.data
            }).catch(() => null)
        },
    },
    getters: {
        mediaViewFolders (state: State): MediaFolder[] {
            return state.mediaViewFolders
        },
        mediaViewMedia (state: State): Media[] {
            return state.mediaViewMedia
        },
        mediaViewMediaCurrentPage (state: State): number {
            return state.mediaViewMediaCurrentPage
        },
        mediaViewMediaLastPage (state: State): number {
            return state.mediaViewMediaLastPage
        },
        mediaViewMediaNextPage (state: State): number {
            return state.mediaViewMediaNextPage
        },
        mediaViewMediaPaginating (state: State): boolean {
            return state.mediaViewMediaPaginating
        },
        mediaViewMediaAttachment (state: State): Media[] {
            //  Filter out all of our media with an attachment folder
            return state.mediaViewMedia.filter((x: Media) => {
                return x.folder === 'attachment'
            })
        },
        mediaViewMediaAttachmentDefaultParams (state: State): MediaParams {
            return state.mediaViewMediaAttachmentDefaultParams
        },
        mediaViewMediaAttachmentParams (state: State): MediaParams {
            return state.mediaViewMediaAttachmentParams
        },
        mediaViewMediaUploaded (state: State): Media[] {
            //  Filter out all of our media with an uploaded folder
            return state.mediaViewMedia.filter((x: Media) => {
                return x.folder === 'uploaded'
            })
        },
        mediaViewMediaUploadedDefaultParams (state: State): MediaParams {
            return state.mediaViewMediaUploadedDefaultParams
        },
        mediaViewMediaUploadedParams (state: State): MediaParams {
            return state.mediaViewMediaUploadedParams
        },
        mediaViewMediaDefaultParams (state: State): MediaParams {
            return state.mediaViewMediaDefaultParams
        },
        mediaViewMediaParams (state: State): MediaParams {
            return state.mediaViewMediaParams
        }
    }
}
